import { useState } from 'react';
import {
  FileText,
  Presentation,
  Calendar,
  MapPin,
  ExternalLink,
  Maximize2,
} from 'lucide-react';
import type { Block } from '../../../types/parcours';

interface EmbedIframeBlock extends Block {
  type: 'embed-iframe';
  src?: string;
  title?: string;
  /** Hauteur (si pas d'aspect-ratio) — défaut 600 */
  height?: number | string;
  /** Aspect ratio (ex: "16/9" pour présentations) — prioritaire sur height */
  aspectRatio?: string;
  /** Type de contenu — utilisé pour le label / l'icône / le sandbox.
   *  Auto-détecté depuis l'URL si absent. */
  kind?: 'form' | 'presentation' | 'calendar' | 'map' | 'other';
}

// Auto-détection du kind depuis l'URL
function detectKind(url: string): NonNullable<EmbedIframeBlock['kind']> {
  const u = url.toLowerCase();
  if (/prezi\.com|genial\.ly|genially|slidesgo|canva\.com\/design|figma\.com\/proto/.test(u))
    return 'presentation';
  if (/calendly\.com|cal\.com|youcanbook\.me|tidycal\.com/.test(u))
    return 'calendar';
  if (/google\.com\/maps|openstreetmap\.org|mapbox\.com|umap/.test(u))
    return 'map';
  if (/hsforms\.net|forms\.gle|tally\.so|typeform\.com|jotform\.com/.test(u))
    return 'form';
  return 'other';
}

const KIND_META: Record<
  NonNullable<EmbedIframeBlock['kind']>,
  { icon: React.ComponentType<{ className?: string }>; label: string; cta: string }
> = {
  form: { icon: FileText, label: 'Formulaire', cta: 'Charger le formulaire' },
  presentation: {
    icon: Presentation,
    label: 'Présentation',
    cta: 'Lancer la présentation',
  },
  calendar: { icon: Calendar, label: 'Calendrier', cta: 'Charger le calendrier' },
  map: { icon: MapPin, label: 'Carte', cta: 'Charger la carte' },
  other: { icon: FileText, label: 'Contenu intégré', cta: 'Charger le contenu' },
};

interface Props {
  block: EmbedIframeBlock;
}

export default function EmbedIframeBlock({ block }: Props) {
  const [loaded, setLoaded] = useState(false);

  if (!block.src) return null;

  const kind = block.kind ?? detectKind(block.src);
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const title = block.title ?? meta.label;

  // Pour les présentations : aspect-ratio prioritaire (sinon 16/9 par défaut)
  const useAspectRatio =
    block.aspectRatio ?? (kind === 'presentation' ? '16/9' : undefined);
  const height = block.height ?? 600;

  // Pour Prezi/Genially/maps : sandbox plus permissif pour autoriser les scripts
  // (sinon les présentations interactives ne fonctionnent pas)
  const sandbox =
    kind === 'presentation' || kind === 'map'
      ? 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms'
      : 'allow-forms allow-scripts allow-same-origin allow-popups';

  function openFullscreen() {
    window.open(block.src, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="my-6 rounded-2xl ring-1 ring-neutral-200 overflow-hidden bg-white shadow-sm">
      {loaded && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200">
          <Icon className="w-4 h-4 text-[#d03484] flex-shrink-0" />
          <p className="flex-1 min-w-0 text-sm font-semibold text-[#30475e] truncate">
            {title}
          </p>
          <button
            type="button"
            onClick={openFullscreen}
            title="Ouvrir en plein écran"
            className="inline-flex items-center gap-1 rounded-lg text-xs font-medium text-neutral-500 hover:text-[#d03484] hover:bg-white px-2 py-1 transition-colors"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Plein écran</span>
          </button>
          <a
            href={block.src}
            target="_blank"
            rel="noopener noreferrer"
            title="Ouvrir dans un nouvel onglet"
            className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-neutral-500 hover:text-[#d03484] hover:bg-white transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}

      {!loaded ? (
        <button
          type="button"
          onClick={() => setLoaded(true)}
          className="w-full flex flex-col items-center justify-center gap-4 py-14 px-6 text-center bg-gradient-to-br from-[#fbfaf8] to-white hover:from-[#fde7f2] hover:to-[#fde2d3] transition-colors group"
        >
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white ring-1 ring-neutral-200 shadow-sm group-hover:shadow-md transition-shadow">
            <Icon className="w-5 h-5 text-[#d03484]" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#30475e] mb-1">{title}</p>
            <p className="text-xs text-neutral-500">
              {meta.label} hébergé·e par un service externe
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold text-white shadow-md group-hover:shadow-lg transition-shadow">
            {meta.cta}
          </span>
        </button>
      ) : useAspectRatio ? (
        <div style={{ aspectRatio: useAspectRatio }} className="w-full bg-neutral-100">
          <iframe
            src={block.src}
            title={title}
            className="w-full h-full"
            style={{ border: 0 }}
            sandbox={sandbox}
            referrerPolicy="strict-origin-when-cross-origin"
            allow="fullscreen; clipboard-write"
            allowFullScreen
            loading="lazy"
          />
        </div>
      ) : (
        <iframe
          src={block.src}
          title={title}
          height={height}
          className="w-full bg-neutral-100"
          style={{ border: 0 }}
          sandbox={sandbox}
          referrerPolicy="strict-origin-when-cross-origin"
          allow="fullscreen; clipboard-write"
          allowFullScreen
          loading="lazy"
        />
      )}
    </div>
  );
}
